import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { ROUTES } from '../constants/routes';

const UserProfile: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const [showDropdown, setShowDropdown] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset avatar error state when user changes
  useEffect(() => {
    if (!user) return;
    setAvatarError(false);
  }, [user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!user) return null;

  return (
    <div className="user-profile" ref={dropdownRef}>
      <button
        className="user-avatar-button"
        onClick={() => setShowDropdown(!showDropdown)}
        type="button"
        aria-label={user.name}
        title={`${user.name} - Click for options`}
      >
        {user.avatar && !avatarError ? (
          <img
            src={user.avatar}
            alt={user.name}
            className="user-avatar"
            onError={() => setAvatarError(true)}
          />
        ) : (
          <div className="user-avatar-placeholder">
            {user.name.charAt(0).toUpperCase()}
          </div>
        )}
      </button>

      {showDropdown && (
        <div className="user-dropdown">
          <div className="user-dropdown-header">
            <div className="user-info">
              {user.avatar && !avatarError ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="user-avatar-large"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <div className="user-avatar-placeholder user-avatar-large">
                  {user.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="user-details">
                <div className="user-name">{user.name}</div>
              </div>
            </div>
          </div>
          <div className="user-dropdown-divider"></div>
          <div className="user-dropdown-actions">
            {isAdmin() && (
              <button
                className="backoffice-button"
                onClick={() => {
                  navigate(ROUTES.BACKOFFICE);
                  setShowDropdown(false);
                }}
                type="button"
              >
                <span className="backoffice-icon">🔧</span>
                Admin Backoffice
              </button>
            )}
            <button
              className="settings-button"
              onClick={() => {
                navigate(ROUTES.SETTINGS);
                setShowDropdown(false);
              }}
              type="button"
            >
              <span className="settings-icon">⚙️</span>
              Settings
            </button>
            <button
              className="logout-button"
              onClick={() => {
                logout();
                setShowDropdown(false);
              }}
              type="button"
            >
              <span className="logout-icon">🚪</span>
              Logout
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
